import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import { ActionCableClient, buildActionCableUrl } from '@core/realtime/action-cable-client';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { SignOutService } from '@core/auth/sign-out.service';
import { ToastService } from 'src/app/shared/ui/toast/toast.service';
import {
  SessionConflictDialogComponent,
  SessionConflictDialogResult,
} from './session-conflict-dialog.component';

interface SessionConflictMessage {
  event: 'session_conflict';
  newSessionId: number;
  device: string | null;
}

interface ForceLogoutMessage {
  event: 'force_logout';
  sessionId: number;
}

function isSessionConflictMessage(message: unknown): message is SessionConflictMessage {
  return (
    typeof message === 'object' &&
    message !== null &&
    (message as { event?: unknown }).event === 'session_conflict'
  );
}

function isForceLogoutMessage(message: unknown): message is ForceLogoutMessage {
  return (
    typeof message === 'object' &&
    message !== null &&
    (message as { event?: unknown }).event === 'force_logout'
  );
}

/**
 * Aviso de sesión única por usuario (estilo WhatsApp Web): escucha `UserSessionChannel` y
 * reacciona a un login nuevo con esta cuenta ("Mantener aquí"/"Salir") o a que ESTA sesión
 * puntual haya sido revocada desde otro lado (cierre inmediato, sin diálogo).
 */
@Injectable({ providedIn: 'root' })
export class SessionConflictService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly authSession = inject(AuthSessionService);
  private readonly signOutService = inject(SignOutService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private client: ActionCableClient | null = null;
  private dialogOpen = false;

  constructor(@Inject(API_BASE_URL) private readonly apiBaseUrl: string) {}

  connect(companyId: number): void {
    const token = this.authSession.token();
    if (!token) {
      return;
    }

    this.disconnect();
    this.client = new ActionCableClient({
      url: buildActionCableUrl(this.apiBaseUrl, token, companyId),
      channel: 'UserSessionChannel',
      onMessage: (message) => this.handleMessage(message),
    });
    this.client.connect();
  }

  disconnect(): void {
    this.client?.disconnect();
    this.client = null;
  }

  private handleMessage(message: unknown): void {
    if (isForceLogoutMessage(message)) {
      if (message.sessionId === this.authSession.sessionId()) {
        this.signOutService.signOutThisSessionOnly();
        this.toast.info(
          this.translate.instant('auth.sessionConflict.closedElsewhereToastTitle'),
          this.translate.instant('auth.sessionConflict.closedElsewhereToastMessage')
        );
      }
      return;
    }

    if (isSessionConflictMessage(message)) {
      this.openConflictDialog(message);
    }
  }

  private openConflictDialog(message: SessionConflictMessage): void {
    if (this.dialogOpen) {
      return;
    }
    this.dialogOpen = true;

    const ref = this.dialog.open(SessionConflictDialogComponent, {
      width: '420px',
      disableClose: true,
      data: { device: message.device },
    });

    ref.afterClosed().subscribe((result?: SessionConflictDialogResult) => {
      this.dialogOpen = false;
      if (result === 'keep-here') {
        this.revokeOtherSession(message.newSessionId);
      } else if (result === 'leave') {
        this.signOutService.signOutThisSessionOnly();
      }
    });
  }

  private revokeOtherSession(sessionId: number): void {
    this.http.post(`${this.apiBaseUrl}${ApiEndpoints.auth.revokeSession(sessionId)}`, {}).subscribe({
      next: () =>
        this.toast.success(
          this.translate.instant('auth.sessionConflict.keptHereToastTitle'),
          this.translate.instant('auth.sessionConflict.keptHereToastMessage')
        ),
      error: () => undefined,
    });
  }
}
